<template>
  <div class="dark:bg-dark-screen-background bg-light-secondary-screen-background overflow-auto h-full w-full">
    <div v-if="!company_summary" class="w-full flex justify-center items-center">
      <div class="w-full text-center max-w-lg bg-white dark:bg-dark-box-background dark:text-white p-8 rounded-lg shadow-lg space-y-4">
        <!-- Spinner Icon -->
        <div class="flex justify-center items-center">
          <svg class="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <!-- Title and Message -->
        <h1 class="text-2xl font-semibold mt-4">
          {{ $t("Fetching Insights") }}
        </h1>
        <p class="text-gray-500 dark:text-gray-300">
          {{ $t("This might take a few moments. Hang tight while we gather your data.") }}
        </p>

        <!-- Progress Bar -->
        <div class="w-full bg-gray-200 rounded-full dark:bg-gray-700 h-2.5 mt-6">
          <div class="bg-blue-500 h-2.5 rounded-full" :style="{ width: `${progress}%` }" />
        </div>
      </div>
    </div>
    <div v-else-if="company_summary" class="xl:flex w-full mt-10">
      <div>
        <Logo v-if="!$utils.Record(component_record).logo" class="inline-block w-20 h-20 p-3 rounded-md cursor-default" />
        <img v-else class="inline-block object-contain h-24 p-3 rounded-lg" :src="`${$utils.Record(component_record).logo}`" alt="">
      </div>
      <div class="ml-10 w-[80%]">
        <div class="text-2xl dark:text-white mb-2">
          {{ company_summary.name }}
        </div>
        <div class="w-full">
          <div class="flex w-full overflow-x-auto no-scrollbar">
            <div
              v-for="(tag, index) in company_summary?.tags"
              :key="index"
              class="mr-2 mb-2 rounded-full whitespace-nowrap bg-white dark:bg-dark-box-background dark:text-white border dark:border-dark-divider-border px-3 py-1">
              {{ tag }}
            </div>
          </div>
        </div>
        <div class="dark:text-white w-full">
          {{ company_summary.description }}
        </div>
      </div>
    </div>

    <div v-if="company_summary" class="xl:hidden w-full mt-10">
      <div class="flex items-center">
        <div class="text-2xl dark:text-white mb-2 ml-6">
          {{ company_summary.name }}
        </div>
      </div>
      <div class="mt-6">
        <div class="flex whitespace-nowrap w-full overflow-x-scroll no-scrollbar">
          <div
            v-for="(tag, index) in company_summary?.tags"
            :key="index"
            class="mr-2 mb-2 rounded-full bg-white dark:bg-dark-box-background dark:text-white border dark:border-dark-divider-border px-3 py-1">
            {{ tag }}
          </div>
        </div>
        <div class="dark:text-white">
          {{ company_summary?.description }}
        </div>
      </div>
    </div>

    <div class="relative">
      <div v-if="company_summary && !subjects.length" class="absolute -mt-7 dark:text-white">
        {{ $t("No headlines found at the moment") }}
      </div>
      <div class="flex mt-10 overflow-x-scroll no-scrollbar gap-x-2 box-wrapper">
        <div v-if="!scrolled_to_start" class="p-5 h-16 w-16 absolute rounded-full ml-2 -left-2 top-[40%]" @click="scrollLeft">
          <ChevronLeft
            class="animate animate-bounce-left cursor-pointer h-6 w-6 text-gray-600 dark:text-dark-body-text hover:text-common-grey"
            aria-hidden="true" />
        </div>

        <div
          v-if="!scrolled_to_end && subjects.length > 1"
          class="p-5 h-16 w-16 absolute rounded-full ml-2 -right-2 top-[40%]"
          @click="scrollRight">
          <ChevronRight
            class="animate animate-bounce-left cursor-pointer h-6 w-6 text-gray-600 dark:text-dark-body-text hover:text-common-grey"
            aria-hidden="true" />
        </div>

        <div v-for="(subject, index) in subjects" :key="index">
          <transition
            mode="out-in"
            enter-active-class="transform ease-out duration-300 transition"
            enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0">
            <div
              class="min-w-[62vw] max-w-[62vw] xl:min-w-[27vw] xl:max-w-[27vw] bg-white dark:bg-dark-box-background dark:text-white p-4 pr-0 rounded-lg shadow-lg">
              <div class="text-lg mb-4 dark:text-white">
                {{ subject }}
              </div>

              <div class="h-52 overflow-y-auto">
                <div v-for="(entry, i) in insights.filter((ins) => ins.subject == subject)" :key="i">
                  <div>
                    <a target="_blank" :href="entry.link" class="text-md dark:text-white">
                      {{ entry.headline }}
                    </a>
                    <div class="text-sm text-common-grey mb-3">
                      {{ entry.publisher_name }} - {{ $moment(entry.publish_date).format("DD MMM  YYYY") }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div v-if="company_summary" class="grid grid-col-1 xl:grid-cols-3 mt-6 gap-x-2 relative">
      <div :class="[`${competitor ? 'col-span-3' : 'col-span-2'}`]">
        <div class="grid grid-col-1 xl:grid-cols-2 gap-x-2">
          <div class="bg-white dark:bg-dark-box-background p-2 rounded-lg">
            <div class="flex justify-between">
              <div class="text-lg dark:text-white ml-2 mt-2 mb-2">
                {{ $t("About") }}
              </div>
              <!-- <div class="m-2">
                <Pencil class="h-5 w-5 text-common-grey cursor-pointer" />
              </div> -->
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Domain") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.domain_name ? company_summary.domain_name : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Location") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.location ? company_summary.location : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Industry") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.category?.industry ? company_summary.category.industry : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Sub Industry") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.category?.sub_industry ? company_summary.category.sub_industry : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Tech") }}
              </div>
              <div v-if="company_summary.tech?.length" class="max-w-[200px] flex flex-wrap gap-y-1">
                <div
                  v-for="(tag, index) in company_summary.tech?.slice(1, 8)"
                  :key="index"
                  class="rounded-full text-xs mr-1 bg-white dark:bg-dark-box-background dark:text-white border dark:border-dark-divider-border px-3 py-1">
                  {{ tag }}
                </div>
                <div v-if="company_summary.tech?.length > 8" class="text-sm ml-1 mt-1 text-light-brand-blue dark:text-dark-brand-blue">
                  +{{ company_summary.tech?.length - 8 }} {{ $t("more") }}
                </div>
              </div>
              <div v-else class="dark:text-white">
                {{ " - " }}
              </div>
            </div>
            <div class="flex justify-between p-2">
              <div class="text-common-grey">
                {{ $t("Linkedin") }}
              </div>
              <a
                target="_blank"
                :href="
                  company_summary?.social_networks?.find((s) => s.name == 'linkedin')
                    ? company_summary?.social_networks?.find((s) => s.name == 'linkedin').link
                    : '#'
                "
                class="dark:text-white">
                {{
                  company_summary?.social_networks?.find((s) => s.name == "linkedin")
                    ? company_summary?.social_networks?.find((s) => s.name == "linkedin").link
                    : " - "
                }}
              </a>
            </div>
          </div>
          <div class="bg-white dark:bg-dark-box-background p-2 rounded-lg mt-2 xl:mt-0">
            <div class="flex justify-between">
              <div class="text-lg dark:text-white ml-2 mt-2 mb-2">
                {{ $t("Highlights") }}
              </div>
              <!-- <div class="m-2">
                <Pencil class="h-5 w-5 text-common-grey cursor-pointer" />
              </div> -->
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Annual revenue") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.annual_revenue ? $utils.truncateNumber(company_summary.metrics.annual_revenue) : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Estimated Annual revenue") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.estimated_annual_revenue ? company_summary.metrics.estimated_annual_revenue : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Employees Range") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.employees_range ? company_summary.metrics.employees_range : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Fiscal Year End") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.fiscal_year_end ? $utils.truncateNumber(company_summary.metrics.fiscal_year_end) : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Alexa US Rank") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.alexa_us_rank ? $utils.truncateNumber(company_summary.metrics.alexa_us_rank) : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Alexa Global Rank") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.alexa_global_rank ? $utils.truncateNumber(company_summary.metrics.alexa_global_rank) : " - " }}
              </div>
            </div>
            <div class="flex justify-between border-b dark:border-dark-divider-border p-2">
              <div class="text-common-grey">
                {{ $t("Market Cap") }}
              </div>
              <div class="dark:text-white">
                {{ company_summary.metrics?.market_cap ? $utils.truncateNumber(company_summary.metrics.market_cap) : " - " }}
              </div>
            </div>
          </div>
        </div>
        <div
          :class="[
            `w-full bg-white rounded-lg dark:bg-dark-box-background mt-2 p-2 ${
              other_details_expanded ? '' : 'max-h-[465px] overflow-hidden'
            } `
          ]">
          <div class="text-lg dark:text-white ml-2 mt-2 mb-2">
            {{ $t("Other Details") }}
          </div>

          <div class="mb-4 border-b p-2 dark:border-dark-divider-border">
            <div class="grid grid-cols-1 xl:grid-cols-2 relative">
              <!-- <div class="m-2 absolute right-2">
                <Pencil class="h-5 w-5 text-common-grey cursor-pointer" />
              </div> -->
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Legal Name") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.legal_name ? company_summary.legal_name : " - " }}
                </div>
              </div>
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Domain Aliases") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.domain_name_aliases?.slice(0, 3).join(", ") }}
                  {{ company_summary?.domain_name_aliases?.length > 3 ? `(${company_summary.domain_name_aliases.length - 3} more)` : `` }}
                </div>
              </div>
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Phone Numbers") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.phone ? `${company_summary?.phone}, ` : "" }}
                  {{ company_summary?.website_phones?.splice(0, 2).join(", ") }}
                </div>
              </div>

              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Email addresses") }}
                </div>
                <div class="dark:text-white">
                  {{
                    company_summary?.website_emails?.length
                      ? `${company_summary.website_emails[0]} ${
                        company_summary?.website_emails?.length > 1 ? ` (${company_summary.website_emails.length - 1} more)` : ""
                      }`
                      : ""
                  }}
                </div>
              </div>
            </div>
          </div>

          <div class="mb-4 border-b p-2 dark:border-dark-divider-border">
            <div class="grid grid-cols-1 xl:grid-cols-2 relative">
              <!-- <div class="m-2 absolute right-2">
                <Pencil class="h-5 w-5 text-common-grey cursor-pointer" />
              </div> -->
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Sector") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.category?.sector ? company_summary.category.sector : " - " }}
                </div>
              </div>
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Industry Group") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.category?.industry_group ? company_summary.category.industry_group : " - " }}
                </div>
              </div>
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("SIC Code") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.category?.sic_code ? company_summary.category.sic_code : " - " }}
                </div>
              </div>

              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("NAICS Code") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.category?.naics_code ? company_summary.category.naics_code : " - " }}
                </div>
              </div>
            </div>
          </div>

          <div class="mb-4 border-b p-2 dark:border-dark-divider-border">
            <div class="grid grid-cols-1 xl:grid-cols-2 relative">
              <!-- <div class="m-2 absolute right-2">
                <Pencil class="h-5 w-5 text-common-grey cursor-pointer" />
              </div> -->
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Street Name") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.geo?.street_name ? company_summary.geo.street_name : " - " }}
                </div>
              </div>
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Street Number") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.geo?.street_number ? company_summary.geo.street_number : " - " }}
                </div>
              </div>
              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("Time Zone") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.time_zone ? company_summary.time_zone : " - " }}
                </div>
              </div>

              <div class="col-span-1 mb-4">
                <div class="text-common-grey text-sm">
                  {{ $t("UTC Offset") }}
                </div>
                <div class="dark:text-white">
                  {{ company_summary?.utc_offset ? company_summary.utc_offset : " - " }}
                </div>
              </div>
            </div>
            <div
              v-if="!other_details_expanded"
              :class="[
                `relative dark:bg-dark-box-background/90 bg-white/90 ${
                  competitor ? 'bottom-20' : 'bottom-14'
                }  p-4 cursor-pointer w-full text-light-brand-blue dark:text-dark-brand-blue`
              ]"
              @click="other_details_expanded = true">
              <div class="relative bottom-4 flex w-full">
                <div class="mx-auto flex">
                  <ChevronLeft class="h-5 w-5 mr-2 text-light-brand-blue dark:text-dark-brand-blue -rotate-90" aria-hidden="true" />
                  <div>{{ $t("Show more") }}</div>
                </div>
              </div>
            </div>

            <div
              v-if="other_details_expanded"
              class="relative top-4 dark:bg-dark-box-background/90 bg-white/90 bottom-14 p-4 cursor-pointer w-full text-light-brand-blue dark:text-dark-brand-blue"
              @click="other_details_expanded = false">
              <div class="relative flex w-full">
                <div class="mx-auto flex">
                  <ChevronLeft class="h-5 w-5 mr-2 text-light-brand-blue dark:text-dark-brand-blue rotate-90" aria-hidden="true" />
                  <div>{{ $t("Show less") }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!competitor"
        class="xl:col-span-1 w-[72vw] xl:w-full bg-white dark:bg-dark-box-background p-2 rounded-lg relative mt-2 xl:mt-0">
        <div class="text-lg dark:text-white ml-2 mt-2 mb-4">
          {{ $t("Competitors") }}
        </div>
        <div v-if="competitors_loading" class="dark:text-white">
          {{ $t("Competitors loading...") }}
        </div>
        <div v-if="!competitors_loading && !competitors.length">
          {{ $t("No competitors to show") }}
        </div>
        <div
          v-for="(competitor, index) in competitors?.slice(
            (competitors_page_no - 1) * (competitor ? 9 : 10),
            competitors_page_no * (competitor ? 9 : 10)
          )"
          :key="index"
          class="flex">
          <div class="w-8 text-md text-common-grey">
            {{ index + 1 + (competitors_page_no - 1) * (competitor ? 9 : 10) }}
          </div>

          <div class="hidden xl:flex border dark:border-dark-divider-border mb-2 rounded-md p-2 w-full">
            <img
              class="p-2 bg-white w-20 rounded-md sm:block"
              :src="competitor.logo_link ? competitor.logo_link : '/assets/company-placeholder.png'"
              alt="company logo">
            <div class="ml-4">
              <div class="dark:text-white">
                {{ competitor.short_name }}
              </div>
              <a target="_blank" class="text-light-brand-blue dark:text-dark-brand-blue underline" :href="`//${competitor.domain_name}`">{{
                competitor.domain_name
              }}</a>
            </div>

            <div v-if="!this.competitor" class="ml-auto">
              <span class="text-common-grey text-sm">Score:</span> <span class="dark:text-white">{{ competitor.score }}</span>
              <div
                class="text-common-grey relative top-1 underline cursor-pointer"
                @click="$emitter.emit(ALL_EMITTERS.SHOW_COMPETITOR_INSIGHTS, { competitor })">
                {{ $t("Show more") }}
              </div>
            </div>
          </div>

          <div class="border xl:hidden dark:border-dark-divider-border mb-2 rounded-md p-2 w-full">
            <div class="flex">
              <img
                class="p-2 bg-white min-w-11 max-w-11 min-h-11 max-h-11 rounded-md sm:block"
                :src="competitor.logo_link ? competitor.logo_link : import(/* @vite-ignore */ '@/assets/company-placeholder.png')"
                alt="company logo">
              <div class="ml-4">
                <div class="dark:text-white">
                  {{ competitor.short_name }}
                </div>
                <a target="_blank" class="text-light-brand-blue dark:text-dark-brand-blue underline" :href="competitor.domain_name">{{
                  competitor.domain_name
                }}</a>
              </div>
            </div>

            <div class="flex items-center mt-1">
              <span class="text-common-grey text-sm">{{ $t("Score") }}:</span> <span class="dark:text-white">{{ competitor.score }}</span>
            </div>
          </div>
        </div>

        <div v-if="competitors?.length > (competitor ? 9 : 10)" class="flex flex-end w-full justify-end cursor-pointer">
          <div v-if="competitors_page_no > 1" class="p-1" @click="competitors_page_no = competitors_page_no - 1">
            <ChevronLeft class="mt-1 h-5 w-5 text-common-grey" aria-hidden="true" />
          </div>
          <div
            v-for="(page, index) in Array(Math.ceil(competitors.length / (competitor ? 9 : 10)))"
            :key="index"
            :class="[
              `p-1 dark:text-white rounded-sm ${
                competitors_page_no == index + 1 ? 'border border-light-brand-blue dark:border-dark-brand-blue' : ''
              }`
            ]"
            @click="competitors_page_no = index + 1">
            {{ index + 1 }}
          </div>
          <div
            v-if="competitors_page_no < Math.ceil(competitors.length / (competitor ? 9 : 10))"
            class="p-1"
            @click="competitors_page_no = competitors_page_no + 1">
            <ChevronRight class="mt-1 h-5 w-5 text-common-grey" aria-hidden="true" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Logo from "@/components/shared/Logo.vue"
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import dataService from "@/services/data.service"
import { ChevronRight, ChevronLeft } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
const MAX_CALLS = 10
export default {
  name: "CompetitorsTab",
  components: {
    Logo,
    ChevronLeft,
    ChevronRight
    // Pencil
  },
  props: {
    competitor: {
      type: Object,
      default: null
    },
    linkedRecord: {
      type: String,
      required: false
    },
    connectTo: {
      type: String,
      required: false
    },
    pageReference: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      ALL_EMITTERS,
      subjects: [],
      insights: [],
      summary_calls_cpt: 0,
      progress: 0,
      competitors_cpt: 0,
      insights_calls_cpt: 0,
      company_summary: null,

      scrolled_to_end: false,
      scrolled_to_start: true,
      other_details_expanded: false,
      competitors_page_no: 1,
      insights_fetching: false,
      competitors_loading: false,
      competitors: [],
      unmounted: false,
      loading: false,
      record_error: false,
      component_record: null
    }
  },

  created() {
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(this.$props, this.postGetComponentRecord)
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record
  },
  mounted() {
    // Simulate loading progress
    const interval = setInterval(() => {
      if (this.progress < 100) {
        this.progress += 1 // Adjust the increment speed as needed
      } else {
        clearInterval(interval)
      }
    }, 50)
  },
  unmounted() {
    this.unmounted = true
  },

  deactivated() {
    this.unmounted = true
  },

  methods: {
    postGetComponentRecord() {
      this.getCompanyInsights()
    },

    scrollRight() {
      let content = document.querySelector(".box-wrapper")
      content.scrollLeft += 150
      const scrolled_to_end = content.scrollWidth <= content.offsetWidth + content.scrollLeft + 150
      this.scrolled_to_end = scrolled_to_end
      this.scrolled_to_start = false
    },

    scrollLeft() {
      let content = document.querySelector(".box-wrapper")
      content.scrollLeft -= 150
      const scrolled_to_start = content.scrollLeft - 150 <= 0
      this.scrolled_to_start = scrolled_to_start
      this.scrolled_to_end = false
    },

    // eslint-disable-next-line complexity
    async asyncGetAll(next, list, params = null, method = "get") {
      if (next) {
        try {
          let result = await dataService.callEndpointByUrl(next, params, method)
          list.push(...result.data.results)
          this.asyncGetAll(result.data.next, list, params, method)
        } catch (_) {
          return null
        }
      } else {
        if (list == this.competitors) {
          this.insights_fetching = false
        } else {
          this.competitors_loading = false
        }
      }
      return null
    },
    getCompanyInsights() {
      this.insights_fetching = true
      this.insights_calls_cpt += 1
      dataService
        .getRecordInsights(this.component_record.id)
        .then((res) => {
          // this.insights = res.data.results

          if (res.data.status != "RECORD_INSIGHTS_GENERATION_INITIATED" && res.data.status != "RECORD_INSIGHTS_NOT_AVAILABLE") {
            this.competitors = res.data.competitors
            this.insights = res.data.news
            this.subjects = []
            this.company_summary = res.data.stats
            this.insights_fetching = true
            res.data.news.forEach((insight) => {
              if (!this.subjects.includes(insight.subject)) {
                this.subjects.push(insight.subject)
              }
            })
            this.insights = res.data.news
          } else if (res.data.status == "RECORD_INSIGHTS_NOT_AVAILABLE") {
            this.insights = []
          } else {
            setTimeout(() => {
              if (this.insights_calls_cpt < MAX_CALLS) {
                this.getCompanyInsights()
              }
            }, 5000)
          }
        })
        .catch(() => {
          this.insights_fetching = false
        })
    }
  }
}
</script>
